import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { ChatRole } from "./types";

interface ChatIdentity {
  role: ChatRole;
  displayName: string;
  grade: string | null;
  loading: boolean;
}

/**
 * Client-side mirror of the server identity — used ONLY to render starters
 * and greetings. The server re-resolves on every chat request and is the
 * source of truth for tool access.
 */
export function useChatIdentity(): ChatIdentity {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<ChatIdentity>({
    role: "guest",
    displayName: "Guest",
    grade: null,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (authLoading) return;
      if (!user) {
        if (!cancelled) setState({ role: "guest", displayName: "Guest", grade: null, loading: false });
        return;
      }
      const [rolesRes, profRes, prefRes, parentRes] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user.id),
        supabase.from("profiles").select("first_name,last_name").eq("id", user.id).maybeSingle(),
        supabase.from("user_learning_preferences").select("grade").eq("user_id", user.id).order("last_updated", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("parents").select("id").eq("user_id", user.id).maybeSingle(),
      ]);
      const roles = (rolesRes.data ?? []).map((r: any) => r.role);
      const role: ChatRole = roles.includes("admin")
        ? "admin"
        : roles.includes("instructor")
        ? "instructor"
        : roles.includes("parent") || parentRes.data
        ? "parent"
        : "student";
      const p = profRes.data as any;
      const displayName = [p?.first_name, p?.last_name].filter(Boolean).join(" ") || user.email?.split("@")[0] || "there";
      if (!cancelled) {
        setState({
          role,
          displayName,
          grade: (prefRes.data as any)?.grade ?? null,
          loading: false,
        });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  return state;
}
